import React, { useEffect } from 'react';

/**
 * DesktopModeWrapper
 * ------------------------------------------------------------
 * Forces child components to behave as if they're running in
 * a "desktop" viewport — even on small/mobile screens.
 *
 * It temporarily overrides `window.matchMedia` so that any
 * query containing "(max-width: ...)" always returns `false`.
 *
 * This ensures components that rely on matchMedia (like certain
 * date pickers or responsive layouts) think they’re on desktop.
 *
 * Usage example:
 *
 * <DesktopModeWrapper>
 *   <DatePicker ... />
 * </DesktopModeWrapper>
 *
 * This is a global override. While this wrapper is mounted,
 * *all* components inside it will see the fake matchMedia result.
 * Make sure to wrap only the parts of your app that need this.
 */
export const DesktopModeWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Store the original matchMedia function
  const originalMatchMedia = window.matchMedia.bind(window);

  // === Override matchMedia synchronously (before rendering children) ===
  // Ensures any component reading matchMedia during initial render
  // sees the "forced desktop" result.
  window.matchMedia = (query: string): MediaQueryList => {
    // If the query checks for a max-width, pretend we're on desktop
    if (query.includes('max-width')) {
      const fakeMatchMedia: MediaQueryList = {
        matches: false,
        media: query,
        onchange: null,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        addListener: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        removeListener: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        addEventListener: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        removeEventListener: () => {},
        dispatchEvent: () => false,
      };
      return fakeMatchMedia;
    }

    // For other queries, call the original matchMedia
    return originalMatchMedia(query);
  };

  // === Restore the original matchMedia when unmounted ===
  useEffect(() => {
    return () => {
      window.matchMedia = originalMatchMedia;
    };
  }, [originalMatchMedia]);

  return <>{children}</>;
};
